/**
 * TestingSetup — provision Configural tenants in the external Testing Application.
 *
 * Shows all Configural tenants/sub-tenants alongside their sync status
 * in the testing app (port 3005 by default). One-click setup creates
 * the matching org → tenant → sub-tenant hierarchy with correct
 * Sandy/runtime connection config.
 */

import React, { useEffect, useState, useCallback } from "react";
import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { useAuth } from "../AuthContext.jsx";

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function TestingSetup() {
  const { currentOrg } = useAuth();
  const orgSlug = currentOrg?.slug || currentOrg?._id || "managecare";

  const [tenants, setTenants] = useState([]);
  const [subTenantMap, setSubTenantMap] = useState({}); // tid → [sub-tenants]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Testing app state
  const [testingUrl, setTestingUrl] = useState(api.getTestingAppUrl());
  const [testingUrlInput, setTestingUrlInput] = useState(api.getTestingAppUrl());
  const [testingConnected, setTestingConnected] = useState(null); // null=unknown, true/false
  const [testingOrg, setTestingOrg] = useState(null); // org doc in testing app
  const [testingTenants, setTestingTenants] = useState({}); // slug → tenant doc
  const [testingSubTenants, setTestingSubTenants] = useState({}); // slug → sub-tenant doc

  // Action state
  const [busy, setBusy] = useState({}); // key → true (for per-row loading)
  const [actionError, setActionError] = useState(null);
  const [runtimeWsUrl, setRuntimeWsUrl] = useState("ws://localhost:3008/ws");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "synced" | "not-setup"
  const [showConfig, setShowConfig] = useState(false);
  const [validating, setValidating] = useState({}); // key → true
  const [validationResults, setValidationResults] = useState({}); // key → { ok, checks[] }

  // ─── Load Configural data ─────────────────────────────────────────

  const loadConfigural = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listTenants();
      const tenantList = res.data || [];
      setTenants(tenantList);

      // Load sub-tenants for each tenant
      const map = {};
      await Promise.all(
        tenantList.map(async (t) => {
          try {
            const subRes = await api.listSubTenants(t._id);
            map[t._id] = subRes.data || [];
          } catch {
            map[t._id] = [];
          }
        })
      );
      setSubTenantMap(map);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Load Testing App data ────────────────────────────────────────

  const loadTestingApp = useCallback(async () => {
    setTestingConnected(null);
    setTestingOrg(null);
    setTestingTenants({});
    setTestingSubTenants({});

    try {
      // Check health
      const health = await api.testingApp.health();
      if (!health) {
        setTestingConnected(false);
        return;
      }
      setTestingConnected(true);

      // Find our org
      const orgsRes = await api.testingApp.listOrgs();
      const orgs = orgsRes.organizations || [];
      const org = orgs.find((o) => o.slug === orgSlug);
      if (!org) {
        setTestingOrg(null);
        return;
      }
      setTestingOrg(org);

      // Load tenants
      const tenantsRes = await api.testingApp.listTenants(org._id);
      const tList = tenantsRes.tenants || [];
      const tMap = {};
      for (const t of tList) tMap[t.slug] = t;
      setTestingTenants(tMap);

      // Load sub-tenants for each testing tenant
      const stMap = {};
      await Promise.all(
        tList.map(async (t) => {
          try {
            const stRes = await api.testingApp.listSubTenants(org._id, t._id);
            for (const st of stRes.subTenants || []) {
              stMap[st.slug] = st;
            }
          } catch {}
        })
      );
      setTestingSubTenants(stMap);
    } catch {
      setTestingConnected(false);
    }
  }, [orgSlug, testingUrl]);

  useEffect(() => {
    loadConfigural();
  }, [loadConfigural]);

  useEffect(() => {
    if (testingUrl) loadTestingApp();
  }, [loadTestingApp, testingUrl]);

  // ─── Actions ──────────────────────────────────────────────────────

  const saveTestingUrl = () => {
    api.setTestingAppUrl(testingUrlInput);
    setTestingUrl(testingUrlInput);
  };

  const setBusyKey = (key, val) => setBusy((prev) => ({ ...prev, [key]: val }));

  const ensureOrg = async () => {
    if (testingOrg) return testingOrg;
    try {
      const org = await api.testingApp.createOrg({ slug: orgSlug, name: currentOrg?.name || orgSlug });
      setTestingOrg(org);
      return org;
    } catch (err) {
      if (err.status === 409) {
        // Already exists, reload
        await loadTestingApp();
        return testingOrg;
      }
      throw err;
    }
  };

  const ensureTenant = async (org, tenant, specialty) => {
    // Use Configural tenant ID as slug for consistency
    const slug = tenant._id || slugify(tenant.name);
    if (testingTenants[slug]) return testingTenants[slug];
    try {
      const t = await api.testingApp.createTenant(org._id, {
        slug,
        name: tenant.name,
        specialty: specialty || "oncology",
      });
      setTestingTenants((prev) => ({ ...prev, [slug]: t }));
      return t;
    } catch (err) {
      if (err.status === 409) {
        await loadTestingApp();
        return testingTenants[slug];
      }
      throw err;
    }
  };

  const setupSubTenant = async (tenant, sub) => {
    const key = `${tenant._id}/${sub._id}`;
    setBusyKey(key, true);
    setActionError(null);
    try {
      const org = await ensureOrg();
      const testTenant = await ensureTenant(org, tenant, sub.specialty);
      if (!testTenant) throw new Error("Failed to create/find tenant in testing app");

      // Use the Configural sub-tenant ID as slug so the compat layer
      // can find it when the runtime calls /tenants/{subTenantId}/...
      const slug = sub._id;
      await api.testingApp.createSubTenant(org._id, testTenant._id, {
        slug,
        name: sub.name,
        specialty: sub.specialty || tenant.specialty || "oncology",
        sandyWsUrl: runtimeWsUrl,
        sandyConfig: {
          sandyTenantId: tenant._id,
          sandySubTenantId: sub._id,
          mode: "text",
          configMode: "draft",
          testTimeout: 480,
          maxConcurrent: 2,
          patientModel: "gemini-2.5-flash",
          verificationModel: "gemini-2.5-flash",
          patientTemperature: 0.7,
        },
        // Don't pass librarySlug — user seeds data manually from testing app
      });
      await loadTestingApp();
    } catch (err) {
      if (err.status !== 409) {
        setActionError(err);
      } else {
        await loadTestingApp();
      }
    } finally {
      setBusyKey(key, false);
    }
  };

  const setupAllForTenant = async (tenant) => {
    const subs = subTenantMap[tenant._id] || [];
    for (const sub of subs) {
      if (!testingSubTenants[sub._id]) {
        await setupSubTenant(tenant, sub);
      }
    }
  };

  // ─── Validate sub-tenant setup ─────────────────────────────────

  const validateSubTenant = async (tenant, sub) => {
    const key = `${tenant._id}/${sub._id}`;
    setValidating((prev) => ({ ...prev, [key]: true }));
    const checks = [];

    try {
      // 1. Check testing app sub-tenant exists
      const testSub = testingSubTenants[sub._id];
      if (!testSub) {
        checks.push({ label: "Sub-tenant exists in testing app", ok: false, detail: "Not found" });
        setValidationResults((prev) => ({ ...prev, [key]: { ok: false, checks } }));
        return;
      }
      checks.push({ label: "Sub-tenant exists in testing app", ok: true, detail: `slug: ${testSub.slug}` });

      // 2. Check sandyConfig exists and has correct IDs
      const cfg = testSub.sandyConfig || {};
      const hasTenantId = !!cfg.sandyTenantId;
      const hasSubTenantId = !!cfg.sandySubTenantId;
      const tenantIdCorrect = cfg.sandyTenantId === tenant._id;
      const subTenantIdCorrect = cfg.sandySubTenantId === sub._id;

      checks.push({
        label: "sandyConfig.sandyTenantId",
        ok: hasTenantId && tenantIdCorrect,
        detail: hasTenantId
          ? `"${cfg.sandyTenantId}" ${tenantIdCorrect ? "✓ matches" : `✗ should be "${tenant._id}"`}`
          : "NOT SET — runtime will fail to load prompts",
      });
      checks.push({
        label: "sandyConfig.sandySubTenantId",
        ok: hasSubTenantId && subTenantIdCorrect,
        detail: hasSubTenantId
          ? `"${cfg.sandySubTenantId}" ${subTenantIdCorrect ? "✓ matches" : `✗ should be "${sub._id}"`}`
          : "NOT SET",
      });

      // 3. Check sandyConfig.configMode
      checks.push({
        label: "sandyConfig.configMode",
        ok: !!cfg.configMode,
        detail: cfg.configMode || "NOT SET (will default to live)",
      });

      // 4. Check configural prompts exist for this sub-tenant
      try {
        const promptsRes = await api.listSubTenantPrompts(tenant._id, sub._id, cfg.configMode || "draft");
        const promptCount = (promptsRes.data || []).length;
        checks.push({
          label: "Configural agent prompts",
          ok: promptCount > 0,
          detail: `${promptCount} agents found (${(promptsRes.data || []).map((p) => p.key).join(", ")})`,
        });
      } catch {
        checks.push({ label: "Configural agent prompts", ok: false, detail: "Failed to load" });
      }

      // 5. Check configural config exists
      try {
        const cfgRes = await api.getSubTenantConfig(tenant._id, sub._id, cfg.configMode || "draft");
        const hasOrch = !!(cfgRes.data?.content?.orchestration?.prompt);
        const hasVars = !!(cfgRes.data?.content?.variables?.practice_name);
        checks.push({
          label: "Orchestration prompt",
          ok: hasOrch,
          detail: hasOrch ? `${cfgRes.data.content.orchestration.prompt.length} chars` : "MISSING — orchestrator will have no instructions",
        });
        checks.push({
          label: "Variables (practice_name)",
          ok: hasVars,
          detail: hasVars ? cfgRes.data.content.variables.practice_name : "NOT SET — will show as empty in prompts",
        });
      } catch {
        checks.push({ label: "Configural config", ok: false, detail: "Failed to load" });
      }

      // 6. Check data service reachable for this sub-tenant
      try {
        const dsRes = await api.testingApp.health();
        checks.push({ label: "Testing app reachable", ok: !!dsRes, detail: "OK" });
      } catch {
        checks.push({ label: "Testing app reachable", ok: false, detail: "Cannot reach testing app" });
      }

      // 7. Check patients exist
      try {
        const pRes = await fetch(`${api.getTestingAppUrl()}/tenants/${sub._id}/patients`).then((r) => r.json());
        const patientCount = (pRes.patients || pRes.data || []).length;
        checks.push({
          label: "Test patients available",
          ok: patientCount > 0,
          detail: patientCount > 0
            ? `${patientCount} patient(s): ${(pRes.patients || pRes.data || []).slice(0, 3).map((p) => `${p.firstName} ${p.lastName}`).join(", ")}`
            : "No patients — verification will fail",
        });
      } catch {
        checks.push({ label: "Test patients", ok: false, detail: "Failed to query" });
      }

      const allOk = checks.every((c) => c.ok);
      setValidationResults((prev) => ({ ...prev, [key]: { ok: allOk, checks } }));
    } catch (err) {
      checks.push({ label: "Validation error", ok: false, detail: err.message });
      setValidationResults((prev) => ({ ...prev, [key]: { ok: false, checks } }));
    } finally {
      setValidating((prev) => ({ ...prev, [key]: false }));
    }
  };

  // ─── Render ───────────────────────────────────────────────────────

  // ─── Build flat rows for the condensed table ───────────────────

  const allRows = [];
  for (const tenant of tenants) {
    const subs = subTenantMap[tenant._id] || [];
    const tenantSlug = slugify(tenant.name);
    const tenantSynced = !!testingTenants[tenantSlug];
    const anyNotSetup = subs.some((s) => !testingSubTenants[s._id]);

    for (const sub of subs) {
      const subSlug = sub._id;
      const synced = !!testingSubTenants[subSlug];
      allRows.push({ tenant, tenantSlug, tenantSynced, anyNotSetup, sub, subSlug, synced });
    }
    if (subs.length === 0) {
      allRows.push({ tenant, tenantSlug, tenantSynced, anyNotSetup, sub: null, subSlug: null, synced: false });
    }
  }

  // Apply search + status filter
  const q = search.trim().toLowerCase();
  const filtered = allRows.filter((r) => {
    if (statusFilter === "synced" && !r.synced) return false;
    if (statusFilter === "not-setup" && r.synced) return false;
    if (q) {
      const hay = `${r.tenant.name} ${r.sub?.name || ""} ${r.sub?.specialty || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Stats
  const totalSubs = allRows.filter((r) => r.sub).length;
  const syncedCount = allRows.filter((r) => r.sub && r.synced).length;
  const notSetupCount = totalSubs - syncedCount;

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Testing Setup"
        subtitle="Provision Configural tenants in the Testing Application for automated test runs."
        breadcrumbs={[{ label: "Testing Setup" }]}
      />

      <div className="page-body">
        <ErrorBanner error={error} />
        <ErrorBanner error={actionError} />

        {/* ── Top bar: connection status + config toggle ─── */}
        <div className="card mb-16" style={{ padding: "10px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Testing App:</span>
            {testingConnected === true && (
              <span style={{ color: "var(--color-success)", fontSize: 12, fontWeight: 600 }}>Connected</span>
            )}
            {testingConnected === false && (
              <span style={{ color: "var(--color-danger)", fontSize: 12, fontWeight: 600 }}>Not reachable</span>
            )}
            {testingConnected === null && (
              <span className="muted small">Checking...</span>
            )}

            <span className="muted">|</span>

            <span style={{ fontSize: 13, fontWeight: 600 }}>Org:</span>
            {testingOrg ? (
              <span style={{ color: "var(--color-success)", fontSize: 12 }}>{testingOrg.slug}</span>
            ) : testingConnected ? (
              <button
                className="btn btn-sm"
                style={{ padding: "2px 8px", fontSize: 11 }}
                onClick={async () => { setActionError(null); try { await ensureOrg(); } catch (e) { setActionError(e); } }}
              >
                Setup Org
              </button>
            ) : (
              <span className="muted small">—</span>
            )}

            <div style={{ flex: 1 }} />

            <button
              className="btn btn-sm"
              onClick={() => setShowConfig(!showConfig)}
              style={{ fontSize: 11 }}
            >
              {showConfig ? "Hide Config" : "Config"}
            </button>
            <button className="btn btn-sm" onClick={() => { loadConfigural(); loadTestingApp(); }}>
              Refresh
            </button>
          </div>

          {/* ── Expandable config section ─── */}
          {showConfig && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--color-border)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 600, minWidth: 110 }}>Testing App URL</label>
                <input
                  type="text"
                  value={testingUrlInput}
                  onChange={(e) => setTestingUrlInput(e.target.value)}
                  placeholder="/testing-app"
                  style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 12, fontFamily: "monospace" }}
                />
                <button className="btn btn-sm" onClick={saveTestingUrl} style={{ fontSize: 11 }}>Save</button>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 12, fontWeight: 600, minWidth: 110 }}>Runtime WS URL</label>
                <input
                  type="text"
                  value={runtimeWsUrl}
                  onChange={(e) => setRuntimeWsUrl(e.target.value)}
                  placeholder="ws://localhost:3008/ws"
                  style={{ flex: 1, padding: 6, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", fontSize: 12, fontFamily: "monospace" }}
                />
              </div>
              <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                Use <code>/testing-app</code> for local dev (proxied to localhost:3005). Direct URLs may fail due to CORS.
              </p>
            </div>
          )}
        </div>

        {/* ── Search + Filter + Stats ─────────────────── */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search tenants or sub-tenants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 12px", fontSize: 13, border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)", minWidth: 220,
            }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { id: "all", label: `All (${totalSubs})` },
              { id: "synced", label: `Synced (${syncedCount})` },
              { id: "not-setup", label: `Not setup (${notSetupCount})` },
            ].map((f) => (
              <button
                key={f.id}
                className={`btn btn-sm ${statusFilter === f.id ? "primary" : ""}`}
                onClick={() => setStatusFilter(f.id)}
                style={{ fontSize: 11, padding: "4px 10px" }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <span className="muted small">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {/* ── Condensed Table ─────────────────────────── */}
        <div className="card">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--color-border)", background: "var(--color-bg-subtle)" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 600 }}>Tenant</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 600 }}>Sub-Tenant</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 600 }}>Specialty</th>
                <th style={{ textAlign: "center", padding: "8px 12px", fontSize: 11, fontWeight: 600 }}>Status</th>
                <th style={{ textAlign: "right", padding: "8px 12px", fontSize: 11, fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 20, textAlign: "center" }} className="muted">No matches</td></tr>
              )}
              {(() => {
                let lastTenantId = null;
                return filtered.map((row, idx) => {
                  const showTenantHeader = row.tenant._id !== lastTenantId;
                  lastTenantId = row.tenant._id;
                  const busyKey = row.sub ? `${row.tenant._id}/${row.sub._id}` : null;
                  const isBusy = busyKey ? !!busy[busyKey] : false;

                  return (
                    <React.Fragment key={row.sub?._id || row.tenant._id}>
                      {/* Tenant separator row */}
                      {showTenantHeader && (
                        <tr style={{ background: "var(--color-bg-subtle)", borderBottom: "1px solid var(--color-border)" }}>
                          <td colSpan={3} style={{ padding: "6px 12px" }}>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>{row.tenant.name}</span>
                            {row.tenantSynced ? (
                              <span style={{ marginLeft: 8, fontSize: 10, color: "var(--color-success)", fontWeight: 600 }}>SYNCED</span>
                            ) : (
                              <span style={{ marginLeft: 8, fontSize: 10, color: "#e65100", fontWeight: 600 }}>NOT SETUP</span>
                            )}
                          </td>
                          <td />
                          <td style={{ padding: "6px 12px", textAlign: "right" }}>
                            {testingConnected && row.anyNotSetup && (
                              <button
                                className="btn btn-sm"
                                style={{ fontSize: 11, padding: "2px 10px" }}
                                onClick={() => setupAllForTenant(row.tenant)}
                              >
                                Setup All
                              </button>
                            )}
                          </td>
                        </tr>
                      )}

                      {/* Sub-tenant row */}
                      {row.sub ? (<>
                        <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                          <td style={{ padding: "6px 12px 6px 24px" }} className="muted small">{row.tenant.name}</td>
                          <td style={{ padding: "6px 12px", fontWeight: 500 }}>{row.sub.name}</td>
                          <td style={{ padding: "6px 12px" }}>
                            <span className="badge" style={{ fontSize: 11 }}>{row.sub.specialty || "—"}</span>
                          </td>
                          <td style={{ padding: "6px 12px", textAlign: "center" }}>
                            {row.synced ? (
                              <span style={{ color: "var(--color-success)", fontWeight: 600, fontSize: 11 }}>Synced</span>
                            ) : (
                              <span style={{ color: "var(--color-text-muted)", fontSize: 11 }}>Not setup</span>
                            )}
                          </td>
                          <td style={{ padding: "6px 12px", textAlign: "right" }}>
                            {!row.synced && testingConnected ? (
                              <button
                                className="btn btn-sm primary"
                                disabled={isBusy}
                                onClick={() => setupSubTenant(row.tenant, row.sub)}
                                style={{ fontSize: 11, padding: "2px 10px" }}
                              >
                                {isBusy ? "..." : "Setup"}
                              </button>
                            ) : row.synced ? (
                              <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
                                <button
                                  className="btn btn-sm"
                                  disabled={!!validating[busyKey]}
                                  onClick={() => validateSubTenant(row.tenant, row.sub)}
                                  style={{ fontSize: 10, padding: "2px 8px" }}
                                >
                                  {validating[busyKey] ? "..." : "Validate"}
                                </button>
                                {validationResults[busyKey] && (
                                  <span style={{
                                    fontSize: 10, fontWeight: 600,
                                    color: validationResults[busyKey].ok ? "var(--color-success)" : "#c62828",
                                  }}>
                                    {validationResults[busyKey].ok ? "✓" : "✗"}
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {/* Validation results row */}
                        {row.synced && validationResults[busyKey]?.checks && (
                          <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                            <td colSpan={5} style={{ padding: "0 12px 8px 24px" }}>
                              <div style={{ background: validationResults[busyKey].ok ? "#f1f8f1" : "#fef5f5", border: `1px solid ${validationResults[busyKey].ok ? "#c8e6c9" : "#ffcdd2"}`, borderRadius: 4, padding: "6px 10px", fontSize: 11 }}>
                                {validationResults[busyKey].checks.map((c, ci) => (
                                  <div key={ci} style={{ display: "flex", gap: 6, alignItems: "flex-start", padding: "2px 0" }}>
                                    <span style={{ color: c.ok ? "#2e7d32" : "#c62828", fontWeight: 700, minWidth: 14 }}>
                                      {c.ok ? "✓" : "✗"}
                                    </span>
                                    <span style={{ fontWeight: 600, minWidth: 180 }}>{c.label}</span>
                                    <span style={{ color: c.ok ? "#555" : "#c62828" }}>{c.detail}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>) : (
                        <tr style={{ borderBottom: "1px solid var(--color-border)" }}>
                          <td style={{ padding: "6px 12px 6px 24px" }} className="muted small">{row.tenant.name}</td>
                          <td colSpan={4} style={{ padding: "6px 12px" }} className="muted small">No sub-tenants</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
